/**
 * ContactVisitHistory — visit log + report download/email controls.
 *
 * Renders on the Contact profile page below the existing contact details.
 * Strictly additive: shows nothing if the contact has no visits yet and the
 * user hasn't pressed Track Visit, so existing contact pages render unchanged
 * for contacts with no mileage activity.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../context/AuthContext';
import {
  Loader2, Trash2, FileDown, FileText, Mail,
} from 'lucide-react';
import { formatDateTime } from '../../lib/utils';

export default function ContactVisitHistory({ contactId, contactName, refreshKey = 0 }) {
  const { get, del, post } = useApi();
  const { token } = useAuth();
  const [visits, setVisits] = useState([]);
  const [summary, setSummary] = useState({ count: 0, total_km: 0, total_amount: 0 });
  const [loading, setLoading] = useState(true);
  const [emailing, setEmailing] = useState(false);

  const fetchVisits = useCallback(async () => {
    setLoading(true);
    try {
      const data = await get(`/mileage/visits?contact_id=${contactId}`);
      setVisits(data?.visits || []);
      setSummary(data?.summary || { count: 0, total_km: 0, total_amount: 0 });
    } catch (e) {
      console.error('failed to fetch visits', e);
    } finally {
      setLoading(false);
    }
  }, [contactId, get]);

  useEffect(() => { fetchVisits(); }, [fetchVisits, refreshKey]);

  async function handleDelete(visitId) {
    if (!window.confirm('Delete this visit?')) return;
    try {
      await del(`/mileage/visits/${visitId}`);
      toast.success('Visit deleted');
      fetchVisits();
    } catch (e) {
      toast.error('Could not delete visit');
    }
  }

  // PDFs are served as Response(media_type=application/pdf) with a forced
  // Content-Disposition; we fetch with auth, build a Blob, and click-download.
  async function downloadReport(kind) {
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, '') || '';
      const url = `${apiBase}/api/mileage/report/${kind}?contact_id=${contactId}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Report failed (${res.status})`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `${kind}-report-${contactId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
    } catch (e) {
      toast.error(e?.message || 'Could not download report');
    }
  }

  async function emailReport(kind) {
    setEmailing(true);
    try {
      const r = await post('/mileage/report/email-handoff', {
        report_kind: kind,
        contact_id: contactId,
      });
      toast.success('Draft created in Email Designer');
      // Soft handoff — surface the design id so they can click through.
      if (r?.design_id) {
        const designerUrl = '/email-designer';
        const w = window.open(designerUrl, '_blank');
        if (!w) {
          toast.info(`Open Email Designer to edit & send (draft id ${r.design_id.slice(0, 8)}).`);
        }
      }
    } catch (e) {
      toast.error('Could not prepare email draft');
    } finally {
      setEmailing(false);
    }
  }

  return (
    <Card className="rounded-sm" data-testid="contact-visit-history">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base font-medium">Visit History</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              {summary.count} visit{summary.count === 1 ? '' : 's'} ·{' '}
              {summary.total_km?.toFixed?.(2) || 0} km ·{' '}
              ${summary.total_amount?.toFixed?.(2) || 0}
            </p>
          </div>
          {summary.count > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => downloadReport('client')}
                data-testid="download-client-report-btn"
              >
                <FileDown className="h-3.5 w-3.5 mr-1.5" /> Client PDF
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => downloadReport('internal')}
                data-testid="download-internal-report-btn"
              >
                <FileText className="h-3.5 w-3.5 mr-1.5" /> Internal PDF
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => emailReport('client')}
                disabled={emailing}
                data-testid="email-client-report-btn"
              >
                <Mail className="h-3.5 w-3.5 mr-1.5" /> Email
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : visits.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            No visits logged yet. Use <strong>Track Visit</strong> above to log
            your first visit to {contactName || 'this contact'}.
          </p>
        ) : (
          <div className="space-y-2">
            {visits.map((v) => (
              <div
                key={v.id}
                className="flex items-start gap-3 p-3 rounded-sm border bg-white"
                data-testid={`visit-row-${v.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium">
                      {formatDateTime(v.visit_date)}
                    </span>
                    <Badge variant="outline" className="rounded-sm text-xs">
                      {v.distance_km?.toFixed?.(2)} km
                    </Badge>
                    <Badge
                      variant="outline"
                      className="rounded-sm text-xs text-emerald-700 border-emerald-200"
                    >
                      ${v.amount_dollars?.toFixed?.(2)}
                    </Badge>
                    <Badge variant="outline" className="rounded-sm text-xs">
                      {v.rate_type === 'custom' ? 'Custom' : 'CRA'} @ ${v.rate_per_km?.toFixed?.(4)}/km
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 truncate">
                    {v.start_address} → {v.end_address}
                  </p>
                  {v.note && (
                    <p className="text-xs text-stone-700 mt-1">{v.note}</p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(v.id)}
                  className="text-destructive hover:text-destructive shrink-0"
                  data-testid={`delete-visit-${v.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
